/**
 * The app's notices — in place of the browser's alert() and confirm() (owner, 16 Oct 2026: "old and
 * tired"). One host, mounted once at the app root; two calls anything can make without React in scope:
 *
 *   notify('Reminder sent to Kevin.')                      → a toast, top right, gone in a few seconds
 *   notify({ title, message, tone, link })                 → a toast that stays while it carries a link
 *   await confirmAction('Remove Helen from the firm?')     → a dialog; true on confirm, false otherwise
 *
 * A string to notify() is read for its tone: a request to the user ("Please…", "Add at least…") is a
 * caution, news of something done ("sent", "saved", "connected") is a success, the rest is information.
 * The look follows the InsightCard idiom — white, a slate outline, colour on the rail and the icon only.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Info, AlertTriangle, XCircle, X, Copy, Check } from 'lucide-react';

export type NoticeTone = 'success' | 'info' | 'warning' | 'error';

export interface NoticeInput {
    tone?: NoticeTone;
    title: string;
    message?: string;
    /** A link worth copying — the invite link — shown in its own block with a copy button. */
    link?: { url: string; label?: string; note?: string };
    /** Milliseconds on screen; 0 keeps it until closed. Defaults by tone, and 0 whenever a link is carried. */
    duration?: number;
}

export interface ConfirmInput {
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** A red confirm button for something hard to undo. */
    danger?: boolean;
}

interface Notice extends Required<Pick<NoticeInput, 'tone' | 'title'>> {
    id: number;
    message?: string;
    link?: NoticeInput['link'];
    duration: number;
}
interface PendingConfirm extends Required<Omit<ConfirmInput, 'danger'>> {
    danger: boolean;
    resolve: (ok: boolean) => void;
}

type Listener = () => void;
const state: { notices: Notice[]; confirm: PendingConfirm | null } = { notices: [], confirm: null };
const listeners = new Set<Listener>();
const emit = () => listeners.forEach((l) => l());
let seq = 0;

function toneOf(text: string): NoticeTone {
    if (/^(please|add at least|choose|select|enter)\b|exceeds|could not|couldn't|not valid|no outstanding|not been|isn't switched on|is not switched on/i.test(text)) return 'warning';
    if (/\b(sent|saved|connected|created|copied|done|updated)\b/i.test(text)) return 'success';
    return 'info';
}
const DEFAULT_MS: Record<NoticeTone, number> = { success: 5_000, info: 6_000, warning: 8_000, error: 10_000 };

export function notify(input: string | NoticeInput): number {
    const n: NoticeInput = typeof input === 'string' ? { title: input, tone: toneOf(input) } : input;
    const tone = n.tone ?? (n.title ? toneOf(n.title) : 'info');
    const id = (seq += 1);
    const duration = n.duration ?? (n.link ? 0 : DEFAULT_MS[tone]);
    state.notices = [...state.notices, { id, tone, title: n.title, message: n.message, link: n.link, duration }];
    emit();
    return id;
}

export function dismissNotice(id: number): void {
    state.notices = state.notices.filter((n) => n.id !== id);
    emit();
}

export function confirmAction(input: string | ConfirmInput): Promise<boolean> {
    const c: ConfirmInput = typeof input === 'string' ? { message: input } : input;
    // One question at a time: a second asker gets "no" rather than a dialog over a dialog.
    if (state.confirm) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
        state.confirm = {
            title: c.title ?? 'Please confirm',
            message: c.message,
            confirmLabel: c.confirmLabel ?? 'Confirm',
            cancelLabel: c.cancelLabel ?? 'Cancel',
            danger: !!c.danger || /\b(remove|delete|suspend|revoke|turn off|leave)\b/i.test(c.message),
            resolve,
        };
        emit();
    });
}

/** For tests: what is on screen. */
export function noticeState(): { notices: Notice[]; confirm: PendingConfirm | null } {
    return state;
}
export function resetNoticesForTests(): void {
    state.notices = [];
    state.confirm = null;
    emit();
}

function useNoticeState() {
    const [, bump] = useState(0);
    useEffect(() => {
        const l = () => bump((n) => n + 1);
        listeners.add(l);
        return () => { listeners.delete(l); };
    }, []);
    return state;
}

const TONE = {
    success: { rail: 'border-l-emerald-500', icon: 'text-emerald-600', Icon: CheckCircle2 },
    info: { rail: 'border-l-blue-500', icon: 'text-blue-600', Icon: Info },
    warning: { rail: 'border-l-amber-500', icon: 'text-amber-600', Icon: AlertTriangle },
    error: { rail: 'border-l-rose-500', icon: 'text-rose-600', Icon: XCircle },
} as const;

const Toast = ({ notice }: { notice: Notice }) => {
    const { rail, icon, Icon } = TONE[notice.tone];
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!notice.duration) return undefined;
        const t = setTimeout(() => dismissNotice(notice.id), notice.duration);
        return () => clearTimeout(t);
    }, [notice.id, notice.duration]);
    const copy = async () => {
        try { await navigator.clipboard.writeText(notice.link!.url); setCopied(true); setTimeout(() => setCopied(false), 2_000); } catch { /* clipboard may be blocked */ }
    };
    return (
        <div
            className={`pointer-events-auto w-full max-w-md bg-white rounded-2xl border border-slate-200 border-l-4 ${rail} shadow-[0_12px_28px_-8px_rgba(16,24,40,0.22)] p-4 flex items-start gap-3 animate-[noticeIn_.22s_ease-out]`}
            role={notice.tone === 'error' ? 'alert' : 'status'}
            data-testid="app-notice"
        >
            <Icon size={20} className={`mt-0.5 shrink-0 ${icon}`} aria-hidden />
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 leading-snug">{notice.title}</p>
                {notice.message && <p className="mt-1 text-sm text-slate-600 leading-relaxed">{notice.message}</p>}
                {notice.link && (
                    <div className="mt-3">
                        {notice.link.label && <p className="text-[11px] font-semibold tracking-wide uppercase text-slate-500 mb-1">{notice.link.label}</p>}
                        <div className="flex items-stretch gap-2">
                            <input
                                readOnly
                                value={notice.link.url}
                                onFocus={(e) => e.currentTarget.select()}
                                className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px] font-mono text-slate-700"
                                aria-label="Link"
                            />
                            <button
                                type="button"
                                onClick={copy}
                                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                            >
                                {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
                                {copied ? 'Copied' : 'Copy link'}
                            </button>
                        </div>
                        {notice.link.note && <p className="mt-1.5 text-[12px] text-slate-500">{notice.link.note}</p>}
                    </div>
                )}
            </div>
            <button type="button" onClick={() => dismissNotice(notice.id)} className="shrink-0 -mr-1 -mt-1 p-1 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Dismiss">
                <X size={16} />
            </button>
        </div>
    );
};

const ConfirmDialog = ({ pending }: { pending: PendingConfirm }) => {
    const confirmRef = useRef<HTMLButtonElement>(null);
    const answer = (ok: boolean) => {
        state.confirm = null;
        emit();
        pending.resolve(ok);
    };
    useEffect(() => {
        confirmRef.current?.focus();
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') answer(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={() => answer(false)} role="presentation">
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-[noticeIn_.2s_ease-out]"
                role="dialog"
                aria-modal="true"
                aria-labelledby="app-confirm-title"
                onClick={(e) => e.stopPropagation()}
                data-testid="app-confirm"
            >
                <div className="flex items-start gap-3">
                    <span className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${pending.danger ? 'bg-rose-50 text-rose-600' : 'bg-blue-50 text-blue-600'}`}>
                        <AlertTriangle size={20} aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2 id="app-confirm-title" className="text-base font-bold text-slate-900">{pending.title}</h2>
                        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{pending.message}</p>
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={() => answer(false)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                        {pending.cancelLabel}
                    </button>
                    <button
                        ref={confirmRef}
                        type="button"
                        onClick={() => answer(true)}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${pending.danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {pending.confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

/** Mounted once, at the app root. */
export const NoticeHost = () => {
    const s = useNoticeState();
    if (typeof document === 'undefined') return null;
    return createPortal(
        <>
            <style>{`@keyframes noticeIn { from { opacity: 0; transform: translateY(-6px) scale(.98); } to { opacity: 1; transform: none; } }`}</style>
            {s.notices.length > 0 && (
                <div className="fixed top-4 right-4 z-[10040] flex flex-col gap-3 w-[min(28rem,calc(100vw-2rem))] pointer-events-none" aria-live="polite">
                    {s.notices.map((n) => <Toast key={n.id} notice={n} />)}
                </div>
            )}
            {s.confirm && <ConfirmDialog pending={s.confirm} />}
        </>,
        document.body,
    );
};
